/**
 * Afrikaans: Dashboard
 *
 * What this file covers: The dashboard and its first-run setup checklist.
 *
 * Tone: Encouraging and brief. Tell the business what is happening and what to do next, in a few words.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const dashboard: Catalog = {
  'dashboard.welcome': 'Welkom',
  'dashboard.title': 'Welkom, {{name}}',
  'dashboard.title.anonymous': 'Welkom terug',
  'dashboard.verifyBanner.title': 'Verifieer jou e-pos',
  'dashboard.verifyBanner.body': 'Jou e-pos is nog nie geverifieer nie. Uitsendings is gedeaktiveer totdat dit wel is.',
  'dashboard.verifyBanner.cta': 'Verifieer nou →',
  'dashboard.metrics.unread': '{{count}} ongelees',
  'dashboard.trend.percent': '{{value}}% teenoor gister',
  'dashboard.trend.points': '{{value}} punte teenoor gister',
  'dashboard.trend.flat': 'Bestendig',
  'dashboard.trend.flat.tooltip': 'Min verandering teenoor die vorige 24 uur.',
  'dashboard.trend.tooltip': '{{current}} in die laaste 24 uur, teenoor {{previous}} die 24 uur daarvoor.',
  'dashboard.metrics.deliveryRate': 'Afleweringskoers',
  'dashboard.metrics.deliveryHint': 'Van boodskappe wat die laaste 24 uur gestuur is',
  'dashboard.metrics.deliveryNone': 'Nog geen boodskappe gestuur nie',
  'dashboard.viewMessages': 'Bekyk boodskappe',
  'dashboard.recent.title': 'Onlangse gesprekke',
  'dashboard.recent.openInbox': 'Open inkassie →',
  'dashboard.recent.empty': 'Nog geen gesprekke nie.',
  'dashboard.recent.noMessages': 'Geen boodskappe',
  'dashboard.quickActions.title': 'Vinnige aksies',
  'dashboard.quickActions.description': 'Spring direk na die algemene take.',
  'dashboard.quickActions.addContacts': 'Voeg kontakte by',
  'dashboard.quickActions.newTemplate': 'Nuwe sjabloon',
  'dashboard.quickActions.newBroadcast': 'Nuwe uitsending',
  'dashboard.quickActions.buildFlow': 'Bou ’n vloei',
  'dashboard.quickActions.attachNumber': 'Heg nommer aan',
  'dashboard.quickActions.topUp': 'Laai beursie',
  'setup.title': 'Kom aan die gang',
  'setup.progress': '{{done}} van {{total}} voltooi',
  'setup.dismiss': 'Maak toe',
  'setup.dismissed': 'Opstelkontrolelys weggesteek. Jy kan dit terugbring by Instellings → Voorkoms.',
  'setup.progress.badge': '{{done}} van {{total}} voltooi',
  'setup.step.done': 'Voltooi',
  'setup.step.todo': 'Nog nie gedoen nie',
  'setup.restore.hint': 'Bring die eerste-keer stappe terug op die paneelbord.',
  'setup.open': 'Open',
  'setup.error': 'Ons kon nie jou vorderingstatus nagaan nie. Sommige stappe mag as onvoltooid wys.',
  'setup.task.number.label': 'Koppel ʼn WhatsApp-nommer',
  'setup.task.number.hint': 'Koppel jou Meta-foonnommer om boodskappe te stuur en te ontvang.',
  'setup.task.number.done': 'Jou WhatsApp-nommer is gekoppel.',
  'setup.task.contacts.label': 'Voeg kontakte by',
  'setup.task.contacts.hint': 'Voer ʼn CSV in of voeg kontakte een vir een by.',
  'setup.task.contacts.done': 'Jy het kontakte in jou rekening.',
  'setup.task.template.label': 'Skep ʼn sjabloon',
  'setup.task.template.hint': 'Stel ʼn boodskapsjabloon op en dien dit by Meta in vir goedkeuring.',
  'setup.task.template.done': 'Jy het ʼn sjabloon geskep.',
  'setup.task.conversation.label': 'Begin ʼn gesprek',
  'setup.task.conversation.hint': 'Stuur ʼn boodskap en begin met jou gehoor gesels.',
  'setup.task.conversation.done': 'Jy het ʼn aktiewe gesprek.',
  'dashboard.subtitle': 'Hier is wat vandag by {{org}} gebeur.',
  'dashboard.lastUpdated': 'Laas opgedateer {{when}}',
  'dashboard.refresh': 'Herlaai',
  'dashboard.loadError': 'Sommige van hierdie bladsy kon nie gelaai word nie. Die syfers hieronder is dalk onvolledig.',
  'dashboard.metrics.unavailable': 'Nie beskikbaar nie',
  'dashboard.metrics.inboxHint': 'Oop gesprekke',
  'dashboard.metrics.contactsHint': 'In jou kontakboek',
  'dashboard.metrics.broadcastsHint': 'Tot dusver gestuur',
  'dashboard.metrics.walletHint': 'Beskikbare krediet',
  'dashboard.metrics.spend7d': 'Bestee oor die laaste 7 dae',
  'dashboard.viewInbox': 'Bekyk inkassie',
  'dashboard.viewContacts': 'Bekyk kontakte',
  'dashboard.viewBroadcasts': 'Bekyk uitsendings',
  'dashboard.viewWallet': 'Bekyk beursie',
  'dashboard.recent.viewAll': 'Bekyk alles',
  'dashboard.recent.error': 'Gesprekke kon nie gelaai word nie.',
  'dashboard.recent.emptyAction': 'Heg ʼn nommer aan',
  'dashboard.quickActions.newBroadcast.hint': 'Stuur ʼn uitsending',
  'dashboard.quickActions.addContacts.hint': 'Voer kontakte in of voeg by',
  'dashboard.quickActions.buildFlow.hint': 'Outomatiseer antwoorde',
  'dashboard.quickActions.newTemplate.hint': 'Bestuur boodskapsjablone',
  'dashboard.quickActions.attachNumber.hint': 'Koppel ʼn WhatsApp-nommer',
  'dashboard.quickActions.topUp.hint': 'Voeg krediet by jou beursie',
  'setup.restore': 'Wys die opstellys',
  'dashboard.sendBlock.title': 'Jou boodskappe word nie afgelewer nie',
  'dashboard.sendBlock.ACCOUNT_NOT_PAYABLE': 'WhatsApp keur elke boodskap op {{number}} af weens \'n faktureringsprobleem aan ons kant. Ons maak dit reg, en jy hoef niks by Meta te doen nie.',
  'dashboard.sendBlock.NUMBER_NOT_REGISTERED': '{{number}} is nie by WhatsApp geregistreer om te stuur nie, dus kom niks wat daarvandaan gestuur word, aan nie.',
  'dashboard.sendBlock.META_REFUSED': 'WhatsApp keur elke boodskap op {{number}} af.',
  'dashboard.sendBlock.alsoOthers': '{{count}} ander nommers word ook geraak.',
  'dashboard.sendBlock.notCharged': 'Jy word nie gehef vir \'n boodskap wat nie afgelewer word nie.',
  'dashboard.sendBlock.action': 'Maak Nommers oop',
  'dashboard.disconnected.title': 'WhatsApp het {{number}} ontkoppel',
  'dashboard.disconnected.action': 'Maak Nommers oop',
  'dashboard.websites.failingTitle': '\'n Webwerf kon nie gelees word nie',
  'dashboard.websites.failingBody': 'Ons kon nie {{names}} vir jou kennisbasis lees nie. Jou assistent antwoord steeds uit wat voorheen gelees is.',
  'dashboard.websites.newTitle': 'Nuwe bladsye op jou webwerf',
  'dashboard.websites.newBody': '{{count}} nuwe bladsye is gevind toe jou webwerf weer gelees is. Voeg hulle by sodat jou assistent hulle ken.',
  'dashboard.websites.action': 'Maak die kennisbasis oop',
  'dashboard.conflicts.title': 'Jou kennisbasis spreek homself teë',
  'dashboard.conflicts.bodyOne': 'Twee dokumente gee verskillende inligting oor een punt, so die bot kan die verkeerde een gee.',
  'dashboard.conflicts.body': 'Dokumente gee verskillende inligting oor {{count}} punte, so die bot kan die verkeerde een gee.',
  'dashboard.conflicts.action': 'Besleg hulle',
  'dashboard.metrics.deliveryHintBroadcasts': 'Uitsendings, laaste 24 uur',
  'dashboard.metrics.broadcastsHintDelivered': 'Tot dusver gestuur · {{count}} afgelewer in 24 uur',
  'dashboard.viewDeliveryReport': 'Bekyk afleweringsverslag',
  'dashboard.sendBlock.ACCOUNT_NOT_PAYABLE.customer': 'WhatsApp wys elke boodskap op {{number}} van die hand omdat jou WhatsApp-rekening geen werkende betaalmetode in Meta het nie. Voeg een by in Meta se betaalinstellings.'
};

export default dashboard;
